'use client';

import { forwardRef, useImperativeHandle } from 'react';
import { useTranslations } from 'next-intl';
import clsx from 'clsx';

import type { BookingBlocs } from '@/types/booked-bet';
import { useBetslipStore } from '@/store/betslip';
import { TicketModel } from '@/hooks/query';
import { decimalOdds } from '@/utils/odds-utils';
import { useBookingOddsType } from '../../hooks/use-booking-odds-type';
import OddsValueBloc from '../../blocs/odds-value-bloc';
import { reservationService } from '../../services/reservation.service'; 

import { BookingInput } from '../booking-input';
import { BookingRulesContainer } from '../booking-rules-container';
import { bookingBetContainerStyle } from './booking-bet-container.style';

interface BookingBetContainerProps {
	ticket: TicketModel;
}

export interface BookingHandle {
	getBookingBlocs(): BookingBlocs;
	/**
	 * Submit the reservation form
	 */
	submitReservation(): void;
}

const oddsMinValueBloc = new OddsValueBloc();
const oddsMaxValueBloc = new OddsValueBloc();

export const BookingBetContainer = forwardRef<BookingHandle, BookingBetContainerProps>(function BookingBetContainer(
	{ ticket }, 
	ref
) {
	const t = useTranslations('General');
	const { isConstrainedOddsType, bookingOddsType } = useBookingOddsType(ticket.PriceType);
	const [checkStake, setBookingData, setIsConfirmBet] = useBetslipStore(state => [
		state.checkStake,
		state.setBookingData,
		state.setIsConfirmBet
	]);
	const betType = Number(ticket.Bettype);

	// Convert values other than MY and DEC to DEC
	const displayOdds = isConstrainedOddsType || reservationService.fixedDecimalBetTypes.includes(betType)
		? Number(ticket.DisplayOdds) 
		: decimalOdds(ticket.originalPrice);

	useImperativeHandle(ref, () => ({
		getBookingBlocs(): BookingBlocs {
			return {
				oddsMinValueBloc,
				oddsMaxValueBloc,
			};
		},
		submitReservation() {
			const { expectedValue: minValue } = oddsMinValueBloc.getBookingValue();
			const { expectedValue: maxValue } = oddsMaxValueBloc.getBookingValue();

			// Validating the rules of odds
			oddsMinValueBloc.validate(maxValue);
			oddsMaxValueBloc.validate(minValue);

			const { isVerified: isOddsMinValueVerified } = oddsMinValueBloc.getBookingValue();
			const { isVerified: isOddsMaxValueVerified } = oddsMaxValueBloc.getBookingValue();

			if (isOddsMinValueVerified && isOddsMaxValueVerified) {
				if (checkStake(false, bookingOddsType)) {
					// Create reservation
					setBookingData({
						bookingOddsType,
						expectedMinOdds: minValue,
						expectedMaxOdds: maxValue
					});
					setIsConfirmBet(true);
				}
			}
		}
	}));

	return (
		<>
			<div className={clsx('scheduled-betting', bookingBetContainerStyle.container)}>
				<div className="scheduled-betting__group">
					<div className={clsx('scheduled-betting__title', bookingBetContainerStyle.title)}>
						<span className="text-title">{t('lbl_ECPOddsRange')}</span>
						<span className="text-odds-type">
							({t(`lbl_OddsTypeMin${bookingOddsType}`)})
						</span>
						{/* Booking rules */}
						<BookingRulesContainer />
					</div>
					<div className="scheduled-betting__content">
						<BookingInput 
							bloc={oddsMinValueBloc}
							title={t('lbl_minbet')}
							value={displayOdds} 
							minMaxType="min"
							betType={betType}
							oddsType={bookingOddsType}
							priceType={ticket.PriceType}
						/>
						<BookingInput 
							bloc={oddsMaxValueBloc}
							title={t('lbl_maxbet')}
							value={displayOdds} 
							minMaxType="max"
							betType={betType}
							oddsType={bookingOddsType}
							priceType={ticket.PriceType}
						/>
					</div>
				</div>
			</div>
		</>
	);
});